'use client'

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  Separator,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  getPageHeader,
  Skeleton,
  useArtifactCanvas,
} from '@yeshuman/sdk'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { NutritionPageSkeleton } from './health-list-page-skeleton'
import { Soup, Plus, Pencil, Trash2 } from 'lucide-react'

import { useDeleteNutritionMeal, useNutritionDailySummary, useNutritionMeals, type MealPayload } from '../hooks/use-nutrition'
import { toast } from 'sonner'

import { NutritionMealEditor } from './nutrition-meal-editor'

function utcDayString(d = new Date()): string {
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function formatConsumed(iso: string) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  } catch {
    return iso
  }
}

function macroBrief(t: MealPayload['macros_total']) {
  const pairs: string[] = []
  if (t?.energy_kcal) pairs.push(`${t.energy_kcal} kcal`)
  if (t?.protein_g) pairs.push(`P ${t.protein_g}g`)
  if (t?.carbohydrate_g) pairs.push(`C ${t.carbohydrate_g}g`)
  if (t?.fat_g) pairs.push(`F ${t.fat_g}g`)
  return pairs.length ? pairs.join(' · ') : null
}

export function NutritionPage() {
  const { title, description } = getPageHeader(
    '/nutrition',
    'Nutrition',
    'Track dietary intake — structured foods and macros.'
  )

  const { openCanvas, closeCanvas } = useArtifactCanvas()

  useEffect(() => {
    return () => {
      closeCanvas()
    }
  }, [closeCanvas])

  const [dayUtc, setDayUtc] = useState(() => utcDayString())
  const { meals, isLoading, refetch } = useNutritionMeals({ day: dayUtc, limit: 100 })
  const { summary, isLoading: summaryLoading } = useNutritionDailySummary(dayUtc)

  const openCreateCanvas = useCallback(() => {
    openCanvas({
      title: 'Log a meal',
      preventAutoFocus: true,
      content: <NutritionMealEditor mode="create" onClose={closeCanvas} onSaved={() => void refetch()} />,
    })
  }, [closeCanvas, openCanvas, refetch])

  const openEditCanvas = useCallback(
    (meal: MealPayload) => {
      openCanvas({
        title: 'Edit meal',
        preventAutoFocus: true,
        content: (
          <NutritionMealEditor mode="edit" initialMeal={meal} onClose={closeCanvas} onSaved={() => void refetch()} />
        ),
      })
    },
    [closeCanvas, openCanvas, refetch]
  )

  const deleteM = useDeleteNutritionMeal()
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const daySummaryText = useMemo(() => macroBrief(summary?.macros_total ?? {}), [summary])

  return (
    <PageContainer>
      <PageHeader leading={<PageHeaderLeading />} title={title} description={description} icon={<Soup className="h-5 w-5" />} />

      <Card>
        <CardHeader className="pb-4">
          <CardTitle>Daily totals (UTC day)</CardTitle>
          <CardDescription>
            Showing meals with consumed_at in <span className="font-mono text-foreground">{dayUtc}</span> UTC boundaries.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="nut-day-picker">
              Day (UTC)
            </label>
            <Input
              id="nut-day-picker"
              type="date"
              value={dayUtc}
              onChange={(e) => setDayUtc(e.target.value || utcDayString())}
            />
          </div>
          <div className="text-sm">
            <div className="text-muted-foreground">Today quick pick</div>
            <div className="mt-1">
              <Button type="button" variant="outline" size="sm" onClick={() => setDayUtc(utcDayString())}>
                Use UTC today ({utcDayString()})
              </Button>
            </div>
          </div>
          <div className="sm:ml-auto">
            <Button type="button" onClick={() => openCreateCanvas()}>
              <Plus className="mr-2 h-4 w-4" /> Add meal
            </Button>
          </div>
        </CardContent>
        <Separator />
        <CardFooter className="flex-col items-start pt-6">
          <p className="text-sm font-medium">Day macro roll-up</p>
          <p className="text-muted-foreground text-sm min-h-[1.25rem]">
            {summaryLoading ? (
              <Skeleton className="inline-block h-4 w-48" aria-busy="true" aria-label="Loading totals" />
            ) : (
              daySummaryText || 'No logged macros for this day yet.'
            )}
          </p>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Today&apos;s meals</CardTitle>
          <CardDescription>Tap Edit to refine lines in the Artifact Canvas beside chat.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <NutritionPageSkeleton />
          ) : meals.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground text-sm">No meals for this UTC day.</div>
          ) : (
            meals.map((meal) => {
              const mb = macroBrief(meal.macros_total)
              const visions = meal.lines.some((ln) => ln.line_type === 'vision_estimated')
              return (
                <div key={meal.id} className="rounded-lg border border-border p-4 space-y-2">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="space-y-1">
                      <p className="font-medium">{meal.display_title || 'Untitled meal'}</p>
                      <p className="text-muted-foreground text-xs">{formatConsumed(meal.consumed_at)}</p>
                      <div className="flex gap-2 flex-wrap">
                        <Badge variant={visions ? 'secondary' : 'outline'}>
                          {visions ? 'Includes vision assists' : 'Manual only'}
                        </Badge>
                        {meal.photo_id ? <Badge variant="outline">Photo</Badge> : null}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={() => openEditCanvas(meal)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button type="button" variant="destructive" size="sm" onClick={() => setDeletingId(meal.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {mb ? (
                    <p className="text-sm">{mb}</p>
                  ) : (
                    <p className="text-muted-foreground text-sm">No macro totals recorded.</p>
                  )}
                  {meal.notes ? (
                    <p className="text-xs text-muted-foreground border rounded-md px-3 py-2 bg-muted/40">{meal.notes}</p>
                  ) : null}
                </div>
              )
            })
          )}
        </CardContent>
      </Card>

      <AlertDialog open={Boolean(deletingId)} onOpenChange={(o) => !o && setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove meal?</AlertDialogTitle>
            <AlertDialogDescription>This deletes the logged meal lines for this timestamp.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const id = deletingId
                if (!id) return
                deleteM.mutate(id, {
                  onSuccess: () => {
                    toast.success('Meal removed.')
                    setDeletingId(null)
                    void refetch()
                  },
                  onError: (e: Error) => toast.error(e.message),
                })
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  )
}
